import { auth0ApiClient } from './auth0ApiClient'
import { Plan, CreatePlanRequest, UpdatePlanRequest, GoalRequest, PlanMeeting } from '@/types/plan'

class PlanService {
  private readonly baseUrl = '/plans'

  /**
   * Create a new draft plan (mentor only)
   */
  async createPlan(data: CreatePlanRequest): Promise<Plan> {
    return auth0ApiClient.post<Plan>(this.baseUrl, data)
  }

  /**
   * Get all plans for a relationship (mentor or mentee)
   */
  async getPlansForRelationship(relationshipId: string): Promise<Plan[]> {
    return auth0ApiClient.get<Plan[]>(`${this.baseUrl}/relationship/${relationshipId}`)
  }

  /**
   * Get a single plan by id
   */
  async getPlan(planId: string): Promise<Plan> {
    return auth0ApiClient.get<Plan>(`${this.baseUrl}/${planId}`)
  }

  /**
   * Get meetings linked to a plan
   */
  async getMeetingsForPlan(planId: string): Promise<PlanMeeting[]> {
    return auth0ApiClient.get<PlanMeeting[]>(`${this.baseUrl}/${planId}/meetings`)
  }

  /**
   * Update a draft plan (mentor only)
   */
  async updatePlan(planId: string, data: UpdatePlanRequest): Promise<Plan> {
    return auth0ApiClient.patch<Plan>(`${this.baseUrl}/${planId}`, data)
  }

  /**
   * Activate a draft plan (mentor only)
   */
  async activatePlan(planId: string): Promise<Plan> {
    return auth0ApiClient.patch<Plan>(`${this.baseUrl}/${planId}/activate`, {})
  }

  /**
   * Delete a draft plan (mentor only)
   */
  async deletePlan(planId: string): Promise<void> {
    return auth0ApiClient.delete<void>(`${this.baseUrl}/${planId}`)
  }

  /**
   * Add a goal to a plan (mentor only) — returns the updated plan
   */
  async addGoal(planId: string, data: GoalRequest): Promise<Plan> {
    return auth0ApiClient.post<Plan>(`${this.baseUrl}/${planId}/goals`, data)
  }

  /**
   * Update a goal (mentor only) — returns the updated plan
   */
  async updateGoal(planId: string, goalId: string, data: GoalRequest): Promise<Plan> {
    return auth0ApiClient.patch<Plan>(`${this.baseUrl}/${planId}/goals/${goalId}`, data)
  }

  /**
   * Delete a goal (mentor only) — returns the updated plan
   */
  async deleteGoal(planId: string, goalId: string): Promise<Plan> {
    return auth0ApiClient.delete<Plan>(`${this.baseUrl}/${planId}/goals/${goalId}`)
  }

  /**
   * Link a task to a goal (mentor only) — returns the updated plan
   */
  async linkTask(planId: string, goalId: string, taskId: string): Promise<Plan> {
    return auth0ApiClient.post<Plan>(`${this.baseUrl}/${planId}/goals/${goalId}/link-task`, { taskId })
  }

  /**
   * Unlink a task from a goal (mentor only) — returns the updated plan
   */
  async unlinkTask(planId: string, goalId: string, taskId: string): Promise<Plan> {
    return auth0ApiClient.delete<Plan>(`${this.baseUrl}/${planId}/goals/${goalId}/tasks/${taskId}`)
  }
}

export const planService = new PlanService()
